/**
 * The fictional organization behind `/demo`: Northstar Labs.
 *
 * Naming note: the review-network sample data already called this org Northstar
 * Labs while the demo dashboard called it "Acme Corp". Two names for one
 * fictional company in one product is the kind of detail a prospect notices, so
 * everything standardises on Northstar here.
 *
 * The fixture is deliberately *shaped*, not random. Our ICP arrives with
 * specific pains (no visibility, review bottlenecks, bus factor, burnout), and a
 * demo that shows a uniformly healthy team demonstrates nothing. So the fixture
 * carries planted findings:
 *
 *   - `payments-service` is a bus-factor risk: one owner, high churn, slow.
 *   - `orders-api` is a review-queue problem: work sits waiting, not in review.
 *   - `web-app` is the healthy flagship, so switching scope shows a real contrast.
 *   - Priya and Marcus absorb most review load, so the review network renders
 *     the two-hub picture rather than an even mesh.
 *
 * Those are the demo's selling moments. Keep them when editing.
 */

import type { Organization, Repository } from '@/types/core';
import { demoIndividualMetricsEnabled } from './settings';

/** Stable id for the synthetic org. Never persisted - see `store/orgStore`. */
export const DEMO_ORG_ID = 'demo-org';

export const DEMO_ORG: Organization = {
  id: DEMO_ORG_ID,
  name: 'Northstar Labs',
  slug: 'northstar-labs',
  settings: {
    performance_insights: { enabled: true, threshold_sensitivity: 'medium', include_recommendations: true },
    // individual_metrics is deliberately NOT here - it is resolved at seed time
    // by `buildDemoOrg()` so it can follow the runtime switch. See
    // `lib/demo/settings.ts` for why it ships off.
  },
  created_at: '2024-02-05T00:00:00Z',
  updated_at: '2026-09-01T00:00:00Z',
  has_github_token: true,
  github_token_status: 'valid',
  github_auth_mode: 'app',
  is_personal: false,
  provider_type: 'github',
};

/**
 * The org as seeded into the store, with the runtime switches applied.
 *
 * A function rather than a const because `individual_metrics` is read from
 * localStorage: `useIndividualMetricsEnabled()` reads `currentOrg.settings`, so
 * the value has to be baked in at the moment the org enters scope, not when
 * this module is first evaluated.
 */
export function buildDemoOrg(): Organization {
  return {
    ...DEMO_ORG,
    settings: {
      ...DEMO_ORG.settings,
      individual_metrics: { enabled: demoIndividualMetricsEnabled() },
    },
  };
}

/**
 * Per-repo behaviour profile. These multipliers are the entire mechanism behind
 * the Scope dropdown: selecting a repo swaps the profile, and every generator
 * reads its baselines from here.
 *
 * Rates (`reviewCoverageBase`, `testFailureRateBase`) are absolute percentages.
 * Everything else is a multiplier applied to a shared baseline.
 */
export interface DemoRepoProfile {
  /** Merged PRs per working day at the org's baseline team size. */
  prsPerDay: number;
  /** Median cycle time in hours. */
  cycleTimeHours: number;
  /** Share of cycle time spent waiting for a reviewer to start (0-1). */
  waitForReviewShare: number;
  /** Percentage of merged PRs that received a review. */
  reviewCoverageBase: number;
  /** Percentage of PR check runs that fail. */
  testFailureRateBase: number;
  /** Average lines changed per PR. */
  avgPrSize: number;
  /** Multiplier on rework/churn. >1 means changes get revisited more. */
  churnMultiplier: number;
  /** Developer ids that contribute to this repo, most active first. */
  contributorIds: string[];
  /** Share of this repo's commits owned by `contributorIds[0]` (0-1). */
  topContributorShare: number;
}

export interface DemoRepo {
  repository: Repository;
  profile: DemoRepoProfile;
}

const repo = (
  id: string,
  name: string,
  language: string,
  createdDaysAgo: number,
  profile: DemoRepoProfile
): DemoRepo => ({
  repository: {
    id,
    organization_id: DEMO_ORG_ID,
    external_id: `demo-${id}`,
    name,
    full_name: `northstar-labs/${name}`,
    default_branch: 'main',
    is_private: true,
    language,
    // Sync state must read as fully healthy, or the pages render their
    // "Data Is On Its Way" banner instead of the metrics we came to show.
    last_synced_at: '2026-09-01T00:00:00Z',
    last_sync_status: 'completed',
    initial_sync_completed: true,
    is_active: true,
    created_at: new Date(Date.UTC(2026, 8, 1) - createdDaysAgo * 86400000).toISOString(),
    updated_at: '2026-09-01T00:00:00Z',
    provider_type: 'github',
  },
  profile,
});

export const DEMO_REPOS: DemoRepo[] = [
  repo('demo-repo-web-app', 'web-app', 'TypeScript', 880, {
    prsPerDay: 5.4,
    cycleTimeHours: 26,
    waitForReviewShare: 0.22,
    reviewCoverageBase: 94,
    testFailureRateBase: 4,
    avgPrSize: 210,
    churnMultiplier: 0.85,
    contributorIds: ['dev-priya', 'dev-sofia', 'dev-tomas', 'dev-ana', 'dev-jonas', 'dev-hana'],
    topContributorShare: 0.24,
  }),
  // The planted bus-factor finding. One owner, oldest repo, slowest and
  // churniest. Selecting it should visibly worsen every headline number.
  repo('demo-repo-payments-service', 'payments-service', 'Go', 1020, {
    prsPerDay: 1.6,
    cycleTimeHours: 91,
    waitForReviewShare: 0.31,
    reviewCoverageBase: 61,
    testFailureRateBase: 13,
    avgPrSize: 430,
    churnMultiplier: 1.9,
    contributorIds: ['dev-dmitri', 'dev-marcus'],
    topContributorShare: 0.82,
  }),
  // The planted review-queue finding: cycle time is mediocre, but most of it is
  // idle waiting rather than active review.
  repo('demo-repo-orders-api', 'orders-api', 'Python', 760, {
    prsPerDay: 3.1,
    cycleTimeHours: 64,
    waitForReviewShare: 0.54,
    reviewCoverageBase: 88,
    testFailureRateBase: 7,
    avgPrSize: 290,
    churnMultiplier: 1.15,
    contributorIds: ['dev-marcus', 'dev-dan', 'dev-yusuf', 'dev-ana', 'dev-lena'],
    topContributorShare: 0.31,
  }),
  repo('demo-repo-platform-infra', 'platform-infra', 'HCL', 640, {
    prsPerDay: 1.2,
    cycleTimeHours: 38,
    waitForReviewShare: 0.28,
    reviewCoverageBase: 90,
    testFailureRateBase: 6,
    avgPrSize: 150,
    churnMultiplier: 1.0,
    contributorIds: ['dev-jonas', 'dev-yusuf', 'dev-kwame'],
    topContributorShare: 0.44,
  }),
  repo('demo-repo-shared-libs', 'shared-libs', 'TypeScript', 520, {
    prsPerDay: 0.7,
    cycleTimeHours: 31,
    waitForReviewShare: 0.25,
    reviewCoverageBase: 96,
    testFailureRateBase: 3,
    avgPrSize: 95,
    churnMultiplier: 0.8,
    contributorIds: ['dev-hana', 'dev-lena'],
    topContributorShare: 0.58,
  }),
];

export const DEMO_REPO_IDS = DEMO_REPOS.map((r) => r.repository.id);

export function findDemoRepo(repoId?: string | null): DemoRepo | undefined {
  if (!repoId) return undefined;
  return DEMO_REPOS.find((r) => r.repository.id === repoId);
}

export interface DemoDeveloper {
  id: string;
  login: string;
  name: string;
  isBot: boolean;
  teamId: string | null;
  /** Relative share of reviews this person GIVES. Drives the network hubs. */
  reviewGiveWeight: number;
  /** Relative share of reviews this person RECEIVES. */
  reviewReceiveWeight: number;
  /** Relative code output. Feeds loc_per_day and contributor ordering. */
  outputWeight: number;
  specialism: string;
}

/**
 * 11 humans and 3 bots.
 *
 * Avatars are generated from initials at render time rather than stored as
 * URLs. The previous fixtures pointed at `avatars.githubusercontent.com/u/1..5`,
 * which are real GitHub accounts belonging to real people - putting strangers'
 * faces on a public marketing page, and making the demo fail to render offline
 * or behind a strict CSP.
 */
export const DEMO_DEVELOPERS: DemoDeveloper[] = [
  // Review hub #1 - absorbs the most review load across the org.
  { id: 'dev-priya', login: 'northstar-priya', name: 'Priya Raman', isBot: false, teamId: 'demo-team-product', reviewGiveWeight: 32, reviewReceiveWeight: 9, outputWeight: 15, specialism: 'Frontend' },
  // Review hub #2.
  { id: 'dev-marcus', login: 'northstar-marcus', name: 'Marcus Webb', isBot: false, teamId: 'demo-team-payments', reviewGiveWeight: 27, reviewReceiveWeight: 11, outputWeight: 14, specialism: 'Backend' },
  // The bus factor. High output, almost no reviews given, one repo.
  { id: 'dev-dmitri', login: 'northstar-dmitri', name: 'Dmitri Volkov', isBot: false, teamId: 'demo-team-payments', reviewGiveWeight: 3, reviewReceiveWeight: 16, outputWeight: 19, specialism: 'Payments' },
  { id: 'dev-dan', login: 'northstar-dan', name: 'Dan Okafor', isBot: false, teamId: 'demo-team-payments', reviewGiveWeight: 9, reviewReceiveWeight: 12, outputWeight: 11, specialism: 'Backend' },
  { id: 'dev-sofia', login: 'northstar-sofia', name: 'Sofia Marchetti', isBot: false, teamId: 'demo-team-product', reviewGiveWeight: 8, reviewReceiveWeight: 11, outputWeight: 12, specialism: 'Frontend' },
  { id: 'dev-tomas', login: 'northstar-tomas', name: 'Tomas Neal', isBot: false, teamId: 'demo-team-product', reviewGiveWeight: 6, reviewReceiveWeight: 10, outputWeight: 10, specialism: 'Frontend' },
  { id: 'dev-ana', login: 'northstar-ana', name: 'Ana Ferreira', isBot: false, teamId: 'demo-team-product', reviewGiveWeight: 5, reviewReceiveWeight: 9, outputWeight: 9, specialism: 'Full stack' },
  { id: 'dev-jonas', login: 'northstar-jonas', name: 'Jonas Lindqvist', isBot: false, teamId: 'demo-team-platform', reviewGiveWeight: 4, reviewReceiveWeight: 7, outputWeight: 8, specialism: 'Infrastructure' },
  { id: 'dev-yusuf', login: 'northstar-yusuf', name: 'Yusuf Demir', isBot: false, teamId: 'demo-team-platform', reviewGiveWeight: 3, reviewReceiveWeight: 6, outputWeight: 7, specialism: 'Infrastructure' },
  { id: 'dev-lena', login: 'northstar-lena', name: 'Lena Hoffmann', isBot: false, teamId: 'demo-team-platform', reviewGiveWeight: 2, reviewReceiveWeight: 5, outputWeight: 6, specialism: 'Tooling' },
  { id: 'dev-hana', login: 'northstar-hana', name: 'Hana Sato', isBot: false, teamId: 'demo-team-product', reviewGiveWeight: 2, reviewReceiveWeight: 5, outputWeight: 6, specialism: 'Design systems' },
  { id: 'dev-kwame', login: 'northstar-kwame', name: 'Kwame Asante', isBot: false, teamId: 'demo-team-platform', reviewGiveWeight: 2, reviewReceiveWeight: 4, outputWeight: 5, specialism: 'Security' },
  // Bots. Only visible when Exclude Bots is unchecked - that toggle has to
  // change something, or a viewer assumes the whole filter bar is decorative.
  { id: 'dev-dependabot', login: 'dependabot[bot]', name: 'dependabot', isBot: true, teamId: null, reviewGiveWeight: 0, reviewReceiveWeight: 3, outputWeight: 7, specialism: 'Dependencies' },
  { id: 'dev-renovate', login: 'renovate[bot]', name: 'renovate', isBot: true, teamId: null, reviewGiveWeight: 0, reviewReceiveWeight: 2, outputWeight: 4, specialism: 'Dependencies' },
  { id: 'dev-review-bot', login: 'northstar-review-bot[bot]', name: 'northstar-review-bot', isBot: true, teamId: null, reviewGiveWeight: 11, reviewReceiveWeight: 0, outputWeight: 1, specialism: 'Automated review' },
];

export const DEMO_HUMANS = DEMO_DEVELOPERS.filter((d) => !d.isBot);
export const DEMO_BOTS = DEMO_DEVELOPERS.filter((d) => d.isBot);

export function demoDevelopers(excludeBots: boolean): DemoDeveloper[] {
  return excludeBots ? DEMO_HUMANS : DEMO_DEVELOPERS;
}

export function findDemoDeveloper(id: string): DemoDeveloper | undefined {
  return DEMO_DEVELOPERS.find((d) => d.id === id);
}

export interface DemoTeam {
  id: string;
  name: string;
  slug: string;
  memberIds: string[];
  repoIds: string[];
}

export const DEMO_TEAMS: DemoTeam[] = [
  {
    id: 'demo-team-product',
    name: 'Product Engineering',
    slug: 'product-engineering',
    memberIds: ['dev-priya', 'dev-sofia', 'dev-tomas', 'dev-ana', 'dev-hana'],
    repoIds: ['demo-repo-web-app', 'demo-repo-shared-libs'],
  },
  {
    id: 'demo-team-payments',
    name: 'Payments',
    slug: 'payments',
    memberIds: ['dev-marcus', 'dev-dmitri', 'dev-dan'],
    repoIds: ['demo-repo-payments-service', 'demo-repo-orders-api'],
  },
  {
    id: 'demo-team-platform',
    name: 'Platform',
    slug: 'platform',
    memberIds: ['dev-jonas', 'dev-yusuf', 'dev-lena', 'dev-kwame'],
    repoIds: ['demo-repo-platform-infra'],
  },
];

export function findDemoTeam(teamId?: string | null): DemoTeam | undefined {
  if (!teamId) return undefined;
  return DEMO_TEAMS.find((t) => t.id === teamId);
}

/**
 * The org-wide profile: a volume-weighted roll-up of the per-repo profiles.
 *
 * Derived rather than hand-written so the "sum of the parts equals the whole"
 * invariant holds by construction. A hand-written roll-up drifts the first time
 * someone tunes one repo, and a prospect who selects each repo in turn and adds
 * them up is exactly the kind of person we are selling to.
 */
export function rollUpProfile(repos: DemoRepo[] = DEMO_REPOS): DemoRepoProfile {
  const totalVolume = repos.reduce((sum, r) => sum + r.profile.prsPerDay, 0);
  const weighted = (get: (p: DemoRepoProfile) => number) =>
    repos.reduce((sum, r) => sum + get(r.profile) * r.profile.prsPerDay, 0) / totalVolume;

  return {
    prsPerDay: Number(totalVolume.toFixed(2)),
    cycleTimeHours: Number(weighted((p) => p.cycleTimeHours).toFixed(1)),
    waitForReviewShare: Number(weighted((p) => p.waitForReviewShare).toFixed(3)),
    reviewCoverageBase: Number(weighted((p) => p.reviewCoverageBase).toFixed(1)),
    testFailureRateBase: Number(weighted((p) => p.testFailureRateBase).toFixed(1)),
    avgPrSize: Math.round(weighted((p) => p.avgPrSize)),
    churnMultiplier: Number(weighted((p) => p.churnMultiplier).toFixed(2)),
    contributorIds: DEMO_HUMANS.map((d) => d.id),
    topContributorShare: 0.19,
  };
}

/**
 * Resolve the active profile for a filter state.
 *
 * Repo scope wins over team scope (the Scope dropdown makes them mutually
 * exclusive); a team rolls up only its own repos.
 */
export function resolveProfile(repoId?: string | null, teamId?: string | null): DemoRepoProfile {
  const scoped = findDemoRepo(repoId);
  if (scoped) return scoped.profile;

  const team = findDemoTeam(teamId);
  if (team) {
    const teamRepos = DEMO_REPOS.filter((r) => team.repoIds.includes(r.repository.id));
    if (teamRepos.length > 0) return rollUpProfile(teamRepos);
  }

  return rollUpProfile();
}

/** Contributors in scope, honouring the bots toggle. */
export function resolveContributors(
  repoId: string | null | undefined,
  teamId: string | null | undefined,
  excludeBots: boolean
): DemoDeveloper[] {
  const scopedRepo = findDemoRepo(repoId);
  const team = findDemoTeam(teamId);

  let humans: DemoDeveloper[];
  if (scopedRepo) {
    humans = scopedRepo.profile.contributorIds
      .map((id) => findDemoDeveloper(id))
      .filter((d): d is DemoDeveloper => d != null);
  } else if (team) {
    humans = team.memberIds
      .map((id) => findDemoDeveloper(id))
      .filter((d): d is DemoDeveloper => d != null);
  } else {
    humans = [...DEMO_HUMANS];
  }

  return excludeBots ? humans : [...humans, ...DEMO_BOTS];
}

/**
 * How much the bots toggle moves the numbers.
 *
 * Direction matters more than magnitude. Including bots must raise PR volume
 * (dependency bumps), raise review coverage (the review bot approves
 * everything) and lower average PR size - because that is what including bots
 * does to a real org, and a viewer who knows the domain checks.
 */
export const BOT_IMPACT = {
  prVolumeMultiplier: 1.18,
  reviewCoveragePoints: 4,
  avgPrSizeMultiplier: 0.84,
  cycleTimeMultiplier: 0.93,
} as const;
